import { Compiler } from '@mode-switch/adapt';
import { DataType, DateTime, ExprCall, ExprForm, ExprFrame, ExprStage } from '@mode-switch/express';
import { Attr, Names, Values } from '@mode-switch/model';
import { EncField, ModifyField } from '@mode-vegas/vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { escapeAttrName } from '@mode/shared/util-js';
import { Switch } from './switch';

export type CompiledFormula = CompiledField | CompiledAttr;

abstract class Compiled {
  /**
   * Do not use outside this file
   */
  readonly __expr: FlamingoTypes.ModeCompiledExpr | undefined;

  /**
   * Data type of what the formula returns.
   * Example: "DATEPART('year', [date])" will return an Integer
   */
  readonly formulaDataType: DataType.Name | undefined;

  /**
   * Returns the name of the field referenced in the known formula. If the formula
   * is not a known type, then this will return undefined. CompiledAttrs will always
   * have an attrName, however, which should be equal to the name of the original switch attr.
   */
  readonly attrName: string | undefined;

  /**
   * Data type of the Attr in the known form, or undefined if not available.
   * Example: "DATEPART('year', [date])" will return an Timestamp
   */
  readonly attrDataType: DataType.Name | undefined;

  /**
   * Default aggregate of the Attr in the known form, or undefined if not available.
   */
  readonly defaultAggregate: ExprCall.Name | undefined;

  /**
   * Default format of the Attr in the known form, or undefined if not available.
   */
  readonly defaultFormat: ChartTypes.TextFormat | undefined;

  /**
   * Default color of the Attr, or undefined if not available.
   */
  readonly defaultColor: ChartTypes.FieldColor | undefined;

  /**
   * Default week start day. Hardcoded for now but we'll
   * soon be resolving these against a formula's view
   */
  readonly defaultWeekStart = this.view?.weekStart ?? DateTime.WeekStart.Sunday;

  /**
   * Default year start month. Hardcoded for now but we'll
   * soon be resolving these against a formula's view
   */
  readonly defaultYearStart = this.view?.yearStart ?? DateTime.YearStart.January;

  /**
   * Returns the top-level referenced field name, if available.
   * @description Does not unwrap calculated or analytic fields. In the case of analytic fields, it will return undefined.
   * Use attrName if the lowest level field name is needed instead.
   * @returns If valid known form, attrName. If unknown form or system formula, then the formula. If invalid, undefined.
   * @example SUM(["calcfield"]) returns "calcfield"
   * @example LOOKUP(ZN(SUM([weight])), -1)) returns undefined
   */
  readonly fieldName: string | undefined;

  /**
   * Friendly name of the formula, for use when displaying to a business user (i.e. a chart/table)
   * @description excludes default aggregate function, as well as AGG function. Quick calcs will return a special description.
   * @example [AggCalc1] returns "AggCalc1"
   * @example SUM([dimension]) returns "dimension"
   * @example 10 + LOOKUP(ZN(SUM([weight])), -5) returns unchanged
   */
  readonly displayName: string;

  /**
   * Returns a friendly-ish version of the parsed/compiled formula.
   * @description Meant for display to analysts and other people who may build reports. Quickcalc functions will not be visible.
   * @example [AggCalc1] returns "AGG(AggCalc1)"
   */
  readonly displayFormula: string;

  readonly isValues: boolean = false;
  readonly isNames: boolean = false;

  /**
   * Switch form, for determining if the formula is one of the known forms
   */
  readonly form: ExprForm | undefined;
  readonly frame: ExprFrame | undefined;

  readonly errors: Error[] = [];

  /**
   * returns true if this is a CompiledField (has an alias)
   */
  readonly isField: boolean | undefined;

  protected constructor(
    protected view: FlamingoTypes.ModeView | undefined,
    public readonly formula: string,
    public readonly varType = Attr.Type.Discrete,
    originalAttrName?: string,
    frame?: ExprFrame
  ) {
    // System fields can't be compiled, so exit early if that's the case
    switch (formula) {
      case Names.Key:
        this.isNames = true;
        break;
      case Values.Key:
        this.isValues = true;
        break;
    }

    this.displayFormula = this.displayName = formula;
    this.attrName = originalAttrName;

    if (this.isSystem) {
      const field = this.isNames ? Names.make() : Values.make();
      this.fieldName = formula;
      this.displayName = field.name;
      this.displayFormula = field.name;
      this.formulaDataType = field.formula.type.name;
      this.varType = field.type;
      return;
    }

    if (view == null) {
      this.errors = [new Error('Query results are unavailable')];
      return;
    }

    const model = Switch.Models.makeFromView(view.name, view);
    const result = Switch.compile(model, view.name, formula);
    this.__expr = Switch.getCompiledExpr(result);

    if (this.__expr != null) {
      this.frame = this.__expr.stage === ExprStage.Analytic ? frame : undefined;

      const { field, error } = Switch.tryField(model, view.name, formula, this.varType, undefined, this.frame);
      if (field !== undefined) {
        this.displayName = field.name;
        this.formulaDataType = field.formula.type.name;
      } else {
        this.errors = [error];
        return;
      }

      this.form = this.__expr.form;

      if (this.__expr.form === ExprForm.Generic) {
        this.fieldName = formula;
      } else {
        this.attrName = Switch.Expressions.getAttrName(this.__expr);
        if (this.attrName != null) {
          const attr = Switch.Attrs.find(model, view.name, this.attrName);
          if (attr != null) {
            this.defaultAggregate = Switch.Attrs.defaultAggregate(attr);
            this.defaultFormat = Switch.Attrs.defaultFormat(attr);
            this.defaultColor = Switch.Attrs.defaultColor(attr);
            this.attrDataType = attr.formula.type.name;
          }
        }

        this.fieldName = Switch.Expressions.getPath(this.__expr);

        if (Switch.Expressions.isAnalytic(this.__expr)) {
          this.displayFormula = this.__expr.expr.name;
        } else {
          this.displayFormula = this.__expr.name;
        }
      }

      switch (this.formulaDataType) {
        case DataType.Name.Boolean:
        case DataType.Name.Regexp:
        case DataType.Name.String: {
          this.varType = Attr.Type.Discrete;
          break;
        }
      }
    } else {
      switch (result.type) {
        case Compiler.Result.Type.ParseFailure:
          this.errors = result.errors;
          break;
        case Compiler.Result.Type.ResolveFailure:
          this.errors = result.errors;
          break;
      }
    }
  }

  /**
   * Returns the Attr type, if available, otherwise the formula's return type.
   */
  get dataType() {
    return this.attrDataType || this.formulaDataType;
  }

  /**
   * Returns whether the any aggregate function exists in the known form.
   * This includes analytic functions like Percent Diff. However, if the aggregate
   * is in a calculated field, this will return false.
   */
  get hasAggFunc() {
    if (Switch.Expressions.isAggregate(this.__expr)) {
      return true;
    }

    if (Switch.Expressions.isAnalytic(this.__expr)) {
      return Switch.Expressions.isAggregate(this.__expr.expr);
    }

    return false;
  }

  /**
   * Returns whether the compiled formula is in the Aggregate stage.
   */
  get isAggregateStage() {
    return this.__expr?.stage === ExprStage.Aggregate;
  }

  /**
   * Returns whether this is a calculated field that contains an aggregate or an analytic function.
   * This is useful for determining if a calculated field is actually a scalar or not.
   */
  get isNonScalarCalc() {
    if (Switch.Expressions.isAnalytic(this.__expr) && Switch.Expressions.isAccess(this.__expr.expr)) {
      return (
        Switch.Expressions.isAggregateStage(this.__expr.expr) || Switch.Expressions.isAnalyticStage(this.__expr.expr)
      );
    } else {
      return (
        (Switch.Expressions.isAggregateStage(this.__expr) || Switch.Expressions.isAnalyticStage(this.__expr)) &&
        Switch.Expressions.isAccess(this.__expr)
      );
    }
  }

  /**
   * Returns true if the formula has a stage of Analytic, false otherwise
   */
  get isAnalyticCalc() {
    return this.__expr?.stage === ExprStage.Analytic;
  }

  /**
   * Returns whether the known form is Pct Difference, Difference, or Pct of Total
   */
  get hasAnalyticFunc() {
    return Switch.Expressions.isAnalytic(this.__expr);
  }

  /**
   * Returns whether the known form uses the DATEPART function
   */
  get hasDatePart(): boolean {
    return Switch.Expressions.isDatePart(this.__expr);
  }

  /**
   * Returns whether the known form uses the DATETRUNC function
   */
  get hasDateTrunc(): boolean {
    return Switch.Expressions.isDateTrunc(this.__expr);
  }

  /**
   * Returns whether the known form uses either DATETRUNC or DATEPART functions
   */
  get hasDateFunc(): boolean {
    return this.hasDatePart || this.hasDateTrunc;
  }

  /**
   * Returns the lookup behavior for the analytic function, or undefined if not an analytic function.
   */
  get relativeTo(): ExprForm.RelativeTo | undefined {
    if (Switch.Expressions.isAnalyticWithRelTo(this.__expr)) {
      return this.__expr.relto;
    }

    return;
  }

  /**
   * Returns the totaling function used in a Pct of Total analytic calculation, or undefined if not an analytic function
   */
  get totalUsing(): ExprForm.TotalUsing | undefined {
    if (Switch.Expressions.isAnalyticWithTotalUsing(this.__expr)) {
      return this.__expr.using;
    }

    return;
  }

  /**
   * Returns the DateTime unit ('YEAR', 'DAY', etc) for the date function, or undefined if no date function is present.
   */
  get dateFuncUnit(): DateTime.Unit | undefined {
    if (Switch.Expressions.isDateTrunc(this.__expr) || Switch.Expressions.isDatePart(this.__expr)) {
      return this.__expr.unit;
    }

    return;
  }

  /**
   * Returns the aggregate function (SUM, COUNT, etc), or undefined if no aggregate function is present.
   */
  get aggFunc() {
    return Switch.Expressions.getAggFunc(this.__expr);
  }

  get weekStart(): DateTime.WeekStart | undefined {
    if (Switch.Expressions.isDateTrunc(this.__expr) || Switch.Expressions.isDatePart(this.__expr)) {
      return this.__expr.weekStart;
    }

    return;
  }

  get yearStart(): DateTime.YearStart | undefined {
    if (Switch.Expressions.isDateTrunc(this.__expr) || Switch.Expressions.isDatePart(this.__expr)) {
      return this.__expr.yearStart;
    }

    return;
  }

  /**
   * Returns true if the formula has a stage of Const, false otherwise
   */
  get isConst() {
    return this.__expr?.stage === ExprStage.Const;
  }

  /**
   * Returns true if the formula has a stage of Scalar, false otherwise
   */
  get isScalar() {
    return this.__expr?.stage === ExprStage.Scalar;
  }

  /**
   * Returns whether the formula can be aggregated (has a stage of Const or Scalar)
   */
  get isAggregateable() {
    return this.isScalar || this.isConst;
  }

  /**
   * Returns whether the formula's return type is numeric
   */
  get isFormulaNumeric() {
    switch (this.formulaDataType) {
      case DataType.Name.Float:
      case DataType.Name.Integer:
      case DataType.Name.Decimal:
        return true;
      default:
        return false;
    }
  }

  get isFormulaDate() {
    if (this.formulaDataType != null) {
      return Switch.Formulas.isDate(this.formulaDataType);
    }

    return false;
  }

  /**
   * Returns whether known form's Attr is a numeric type
   */
  get isAttrNumberType() {
    switch (this.attrDataType) {
      case DataType.Name.Float:
      case DataType.Name.Integer:
      case DataType.Name.Decimal:
        return true;
      default:
        return false;
    }
  }

  /**
   * Returns whether known form's Attr is a date type
   */
  get isAttrDateType() {
    switch (this.attrDataType) {
      case DataType.Name.Timestamp:
      case DataType.Name.DateTime:
        return true;
      default:
        return false;
    }
  }

  /**
   * Returns the formula is Measure Names or Measure Values
   */
  get isSystem(): boolean {
    return this.isNames || this.isValues;
  }

  get isDiscrete(): boolean {
    return this.varType === Attr.Type.Discrete;
  }

  get isContinuous(): boolean {
    return this.varType === Attr.Type.Continuous;
  }

  /**
   * Returns whether the formula was able to be compiled
   */
  get isValid() {
    return this.isSystem || (this.__expr ? this.errors.length === 0 : false);
  }

  get isBinned(): boolean {
    return Switch.Expressions.isBin(this.__expr);
  }

  get binSize(): number | void {
    if (Switch.Expressions.isBin(this.__expr)) {
      return this.__expr.size;
    }
  }

  /**
   * Creates a new CompiledFormula with the provided formula, keeping all
   * other properties the same, if possible.
   * @param formula
   */
  abstract withNewFormula(formula: string): CompiledField;

  toEncField(): EncField {
    return {
      variable: { type: Switch.Compat.makeVariableType(this.varType) },
      formula: this.formula,
      frame: this.frame,
    };
  }
}

export class CompiledAttr extends Compiled {
  static fromAttr(view: FlamingoTypes.ModeView, attr: FlamingoTypes.ModeAttr) {
    if (attr == null) {
      throw new Error('attr is required');
    }

    switch (attr.source) {
      case Attr.Source.Values:
      case Attr.Source.Names:
        return new CompiledAttr(view, attr, true);
      default:
        return new CompiledAttr(view, attr, false);
    }
  }

  override readonly isField = false;

  /**
   * For internal use only. Use one of the static from* methods to construct.
   */
  constructor(
    protected override view: FlamingoTypes.ModeView,
    public readonly attr: FlamingoTypes.ModeAttr, // For backwards compatibility
    public systemFormula?: boolean
  ) {
    super(view, systemFormula ? attr.formula.source : `[${escapeAttrName(attr.name)}]`, attr.type, attr.name);
  }

  get source() {
    return this.attr.source;
  }

  get role() {
    return this.attr.role;
  }

  get isCalc() {
    return this.attr.source === Attr.Source.Calc;
  }

  get isLocal() {
    return this.attr.isLocal;
  }

  get isColumn() {
    return this.attr.source === Attr.Source.Column;
  }

  withNewFormula(formula: string, varType?: Attr.Type): CompiledField {
    return new CompiledField(this.view, formula, void 0, varType || this.varType);
  }
}

export class CompiledField extends Compiled {
  /**
   * Only for use for generating an ephemeral field that won't be saved into an encoding.
   * Currently does not generate an alias.
   */
  static fromFormula(view: FlamingoTypes.ModeView | undefined, formula: string, varType: Attr.Type, frame?: ExprFrame) {
    return new CompiledField(view, formula, void 0, varType, frame);
  }

  static fromField(view: FlamingoTypes.ModeView, field: FlamingoTypes.ModeField) {
    const frame = field.source === Attr.Source.Field ? field.frame : undefined;
    return new CompiledField(view, field.formula.source, field.alias, field.type, frame);
  }

  static fromEncField(view: FlamingoTypes.ModeView, field: EncField) {
    return new CompiledField(
      view,
      field.formula,
      field.id,
      Switch.Compat.fromVegasVarType(field.variable.type),
      field.frame
    );
  }

  /**
   * Returns true if the formula contains an Attr that is a Calc source in the provided
   * ModeView
   */
  static formulaHasCalcAttr(view: FlamingoTypes.ModeView, formula: string): boolean {
    const compiled = this.fromFormula(view, formula, Attr.Type.Discrete); // Attr type doesn't matter
    if (!compiled.isValid) {
      return false; // if it isn't valid, can't find the Attr, so assume it isn't a calc
    }
    const attr = view.attrs.find((a) => a.name === compiled.attrName);
    return attr != null && attr.source === Attr.Source.Calc;
  }

  override readonly isField = true;

  /**
   * For internal use only. Use one of the static from* methods to construct.
   */
  constructor(
    view: FlamingoTypes.ModeView | undefined,
    formula: string,
    /**
     * The alias (vegas encoding "id") of the field
     */
    public readonly alias: string | undefined,
    varType = Attr.Type.Discrete,
    frame?: ExprFrame
  ) {
    super(view, formula, varType, undefined, frame);
  }

  /**
   * Creates a new CompiledFormula with the provided formula, keeping all
   * other properties the same, if possible.
   * @param formula
   * @param varType If provided, the new CompiledField will have its type updated
   */
  withNewFormula(formula: string, varType?: Attr.Type): CompiledField {
    return new CompiledField(this.view, formula, this.alias, varType || this.varType, this.frame);
  }

  toModifyField(): ModifyField {
    if (this.alias == null) {
      throw new Error('EncField must have id in order to modify');
    }

    return {
      id: this.alias,
      variable: { type: Switch.Compat.makeVariableType(this.varType) },
      formula: this.formula,
      frame: this.frame,
    };
  }
}
